import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { AIConfig } from '@octery/shared';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const WINDOW_HOURS = [1, 2, 8, 24];

function isActive(until: string | null | undefined): boolean {
  return !!until && new Date(until).getTime() > Date.now();
}

/**
 * /start greeting and the temporary test-mode window. While the window is
 * open, /reset and /start reset the dialog; otherwise /reset is plain text
 * and /start returns the greeting. The window closes by itself.
 */
export function DialogCommandsCard({ config }: { config: AIConfig | undefined }) {
  const queryClient = useQueryClient();
  const [welcome, setWelcome] = useState('');

  useEffect(() => {
    if (config) setWelcome(config.welcomeMessage ?? '');
  }, [config]);

  const mutation = useMutation({
    mutationFn: (data: { testModeUntil?: string | null; welcomeMessage?: string | null }) =>
      api.patch<AIConfig>('/api/ai-settings', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-settings'] });
      toast.success('Saved');
    },
    onError: () => {
      toast.error('Failed to save');
    },
  });

  const active = isActive(config?.testModeUntil);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Dialog commands (/start, /reset)</CardTitle>
        <CardDescription>
          By default a client's /reset is treated as a normal message and /start returns the
          greeting below without touching the dialog. Open the test window to let /reset and /start
          reset the dialog for testing; it closes by itself.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-medium">Greeting for /start</label>
          <textarea
            className="border-input bg-background min-h-[80px] w-full rounded-md border px-3 py-2 text-sm"
            maxLength={1000}
            placeholder="Leave empty to use the default greeting"
            value={welcome}
            onChange={(e) => setWelcome(e.target.value)}
          />
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => mutation.mutate({ welcomeMessage: welcome.trim() || null })}
              disabled={mutation.isPending || !config}
            >
              Save greeting
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">Test window</p>
          <p className="text-xs text-muted-foreground">
            {active
              ? `Open until ${new Date(config!.testModeUntil!).toLocaleString()}.`
              : 'Closed: /reset does nothing, /start returns the greeting.'}
          </p>
          <div className="flex flex-wrap gap-2">
            {WINDOW_HOURS.map((h) => (
              <Button
                key={h}
                type="button"
                variant="outline"
                disabled={mutation.isPending || !config}
                onClick={() =>
                  mutation.mutate({ testModeUntil: new Date(Date.now() + h * 3_600_000).toISOString() })
                }
              >
                Open for {h} h
              </Button>
            ))}
            <Button
              type="button"
              variant="destructive"
              disabled={mutation.isPending || !active}
              onClick={() => mutation.mutate({ testModeUntil: null })}
            >
              Close now
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
